'use client';

import { Toolbar, ToolbarItem } from '@ariakit/react';
import clsx from 'clsx';
import { FieldValues, Path, useController } from 'react-hook-form';
import DallasText from '../../Text/DallasText';
import { AbstractControl } from '../abstract-control';

import styles from './DallasSentimentRatingField.module.scss';

export enum Sentiment {
  VeryDissatisfied = 'very_dissatisfied',
  Dissatisfied = 'dissatisfied',
  Neutral = 'neutral',
  Satisfied = 'satisfied',
  VerySatisfied = 'very_satisfied',
}

const SENTIMENT_EMOJIS = Object.freeze({
  [Sentiment.VeryDissatisfied]: '😡',
  [Sentiment.Dissatisfied]: '😟',
  [Sentiment.Neutral]: '🙂',
  [Sentiment.Satisfied]: '😀',
  [Sentiment.VerySatisfied]: '😍',
});

export interface Props<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
> extends Pick<
    AbstractControl<TName, Sentiment | null>,
    'className' | 'name' | 'disabled' | 'required' | 'onChange'
  > {
  sentimentLabels: Record<Sentiment, string>;
}

export default function DallasSentimentRatingField<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  className,
  name,
  sentimentLabels,
  required,
  disabled: isDisabled,
  onChange,
}: Props<TFieldValues, TName>) {
  const {
    field: { value, onChange: setValue },
    fieldState: { error },
  } = useController<TFieldValues, TName>({
    name,
    rules: { required },
  });

  return (
    <Toolbar role="listbox" className={clsx(className, styles.root)}>
      <div className={clsx(styles.options, error && styles.error)}>
        {Object.values(Sentiment).map((sentiment) => {
          const isSelected = value === sentiment;

          return (
            <ToolbarItem
              key={sentiment}
              className={styles.option}
              onClick={() => {
                setValue(isSelected ? null : sentiment);
                onChange?.(isSelected ? null : sentiment);
              }}
              disabled={isDisabled}
              aria-selected={isSelected}
            >
              <DallasText variant="headerRegularS">
                {SENTIMENT_EMOJIS[sentiment]}
              </DallasText>
              <DallasText>{sentimentLabels[sentiment]}</DallasText>
            </ToolbarItem>
          );
        })}
      </div>
      {error?.type === 'required' && (
        <DallasText variant="captionL" color="error">
          Selection required
        </DallasText>
      )}
    </Toolbar>
  );
}
